import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BMWService = () => {
  const bmwFeatures = [
    'Latest BMW Diagnostics',
    'OEM & OE Parts',
    '1 Year/12,000 Mile Warranty',
    'All BMW Models'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>BMW Repair Dallas | Expert BMW Service Dallas TX | Paradise-Performance</title>
        <meta name="description" content="BMW repair in Dallas that actually makes sense. We work on BMWs all day, every day. Real diagnostics, honest pricing, and we'll tell you what you actually need—not what the dealership wants to sell you. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/bmw" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/bmw" />
        <meta property="og:title" content="BMW Repair Dallas | Expert BMW Service & Maintenance" />
        <meta property="og:description" content="Dallas BMW specialists. Factory diagnostics, all models serviced. Honest pricing, 30-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/bmw-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BMW Repair Dallas | Expert BMW Service & Maintenance" />
        <meta name="twitter:description" content="Dallas BMW specialists. Factory diagnostics, all models serviced. Honest pricing, 30-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/bmw-repair.webp" />

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does BMW maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "BMW maintenance costs average $968 to $1,800 per year depending on the model. An oil change runs $135-$175 at an independent shop (vs $200-$350 at dealerships). Major services like brake jobs cost $350-$800 for pads and rotors. At Paradise-Performance in Dallas, we're typically 30-40% less than BMW dealership prices while using OEM-quality parts."
                }
              },
              {
                "@type": "Question",
                "name": "What is the BMW maintenance schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "BMW recommends service every 10,000 miles or 12 months via their Condition Based Service (CBS) system. Key intervals: Oil change every 7,500-10,000 miles, brake fluid every 2 years, coolant every 4 years, spark plugs every 60,000 miles, and transmission fluid every 60,000 miles (despite BMW's 'lifetime' claim). Dallas heat means more frequent coolant checks."
                }
              },
              {
                "@type": "Question",
                "name": "Can I take my BMW to Jiffy Lube for an oil change?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Technically yes, but we don't recommend it. BMWs require specific European-spec oil (LL-01 or LL-04 rated) and OEM-spec filters. Quick lube shops often use generic oil and filters that don't meet BMW specifications. This can cause premature wear and potentially void your warranty. A BMW specialist shop uses the correct oil and properly resets your service indicators."
                }
              },
              {
                "@type": "Question",
                "name": "How long is the BMW warranty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "New BMW vehicles come with a 4-year/50,000-mile limited warranty covering defects in materials and workmanship. The powertrain is included in this coverage. Rust perforation is covered for 12 years with unlimited mileage. BMW also offers extended warranty options (BMW Extended Protection) for up to 7 years/100,000 miles."
                }
              },
              {
                "@type": "Question",
                "name": "What is the BMW 3 Series maintenance schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "BMW 3 Series maintenance includes: Oil and filter every 10,000 miles, engine air filter every 40,000 miles, cabin filter every 20,000-40,000 miles, brake fluid every 2 years, spark plugs at 60,000 miles (turbo models may need sooner), and transmission fluid at 60,000 miles. Annual inspections should include brakes, suspension, and cooling system—especially important in Dallas heat."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="BMW"
        brandColor="#0066cc"
        tagline="Expert Repair & Service"
        description="Paradise-Performance is your trusted BMW specialist in North Texas. Expert technicians with extensive experience on all BMW models."
        features={bmwFeatures}
        heroImage="/assets/assets/images/bmw-repair.webp"
        heroImageAlt="BMW M5 Performance Vehicle"
        heroVariant="default"
      />


      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">BMW Repair Dallas - Done Right</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center mb-4">
              Look, we get it. You bought a BMW because you wanted something special. The last thing you need is some shop treating it like a Camry. We've been working on BMWs in the Dallas area for years, and we've seen what happens when people take shortcuts.
            </p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center">
              Whether you're coming from Highland Park with a new M5 or North Dallas with a 15-year-old 3 Series that just won't die, we treat every BMW the same way—with the respect it deserves and the knowledge it requires.
            </p>
          </motion.div>

          {/* Image Gallery Section */}
          <motion.div {...fadeInUp} className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-turbo-leak.webp"
                  alt="BMW turbo repair specialist"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-white font-bold">BMW Turbo & Engine Specialists</p>
                </div>
              </div>
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-tensioner-pulleys.webp"
                  alt="BMW belt and pulley service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-white font-bold">Precision Belt & Tensioner Service</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-12">
            <h3 className="text-3xl font-semibold text-gray-900 mb-6">Comprehensive BMW Repair Services in Dallas</h3>
            <p className="text-gray-700 mb-6">
              We specialize in all BMW models, including the 3 Series, 5 Series, X5, and M-performance vehicles. Here's what we offer:
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h4 className="text-xl font-bold text-blue-600 mb-3">BMW Oil Changes</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Your BMW needs the right oil. Not the cheap stuff, not the "close enough" stuff. Our <Link to="/services/oil-change" className="text-blue-600 hover:underline">oil change service</Link> uses what BMW actually specifies—usually Liqui Moly or Castrol Edge, depending on your engine.
                </p>
                <ul className="space-y-1 text-sm mb-3">
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Full synthetic (the real deal, not synthetic blend)</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Proper BMW filter—not an AutoZone special</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>We check everything while we're under there</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Reset your service lights so they stop nagging you</span></li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h4 className="text-xl font-bold text-blue-600 mb-3">BMW Brakes</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Brakes squealing? Pedal feel weird? Yeah, BMW brakes are particular. The good news is we do these all the time. The bad news is you can't cheap out on brake pads—trust us, we've seen what happens.
                </p>
                <ul className="space-y-1 text-sm mb-3">
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Quality pads that won't squeal (usually Akebono or OEM)</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Rotors done right—resurface or replace depending on condition</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Brake fluid flush if it's been a while</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Check your ABS sensors while we're at it</span></li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h4 className="text-xl font-bold text-blue-600 mb-3">Check Engine Lights & Diagnostics</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Christmas tree dashboard? We've got the BMW-specific scanner that actually tells us what's wrong. Not the generic code reader from O'Reilly's—the real deal that talks to every module in your car.
                </p>
                <ul className="space-y-1 text-sm mb-3">
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>ISTA diagnostics (same system BMW dealers use)</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>We'll tell you what's actually wrong, not just throw parts at it</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Transmission issues, electrical gremlins, whatever it is</span></li>
                  <li className="flex items-start gap-1"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /><span>Software updates when needed</span></li>
                </ul>
              </div>
            </div>

            <h4 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Additional BMW Services:</h4>
            <p className="text-gray-700 mb-4">
              As your trusted <Link to="/services/european-car-repair" className="text-blue-600 hover:underline">European auto repair Dallas</Link> specialists, we offer comprehensive BMW services:
            </p>
            <ul className="grid md:grid-cols-2 gap-3">
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" /><span><strong>Cooling System Repairs</strong> (common failure point for BMWs)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" /><span><strong><Link to="/services/transmission-repair" className="hover:underline">Transmission Service</Link></strong> (ZF 8-speed fluid changes)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" /><span><strong>Electrical Diagnostics</strong> (iDrive, battery, alternator)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" /><span><strong><Link to="/services/suspension-repair" className="hover:underline">Suspension Service</Link></strong> (tuning, M-sport upgrades)</span></li>
            </ul>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-12">
            <h3 className="text-3xl font-semibold text-gray-900 mb-6">Common BMW Repairs We Handle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h4 className="font-semibold mb-3 text-lg">Engine & Performance</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Valve cover gasket leaks</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Oil filter housing gasket</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>VANOS system repairs</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Turbocharger issues</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Fuel injector problems</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Timing chain replacement</span></li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h4 className="font-semibold mb-3 text-lg">Electrical & Electronics</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>iDrive system issues</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Window regulator failure</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Battery registration</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Sensor malfunctions</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Module programming</span></li>
                  <li className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-blue-500" /><span>Adaptive headlight repair</span></li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-12">
            <h3 className="text-3xl font-semibold text-gray-900 mb-6">Why Dallas BMW Owners Drive to Paradise</h3>
            <p className="text-gray-700 mb-6">
              Here's the thing about BMW repair in Dallas—you've got two choices that both suck. Pay the dealership $200/hour for an oil change, or take it to some shop that works on everything from Hondas to Hummers and hope they figure it out.
            </p>
            <p className="text-gray-700 mb-6">
              We're the third option. We work on European cars all day. We've got the right tools, the right software, and honestly, we've just seen these problems so many times we can usually tell you what's wrong before we even hook up the scanner.
            </p>
            <p className="text-gray-700 italic">
              And yeah, we're about 30-40% cheaper than the dealerships. Because we don't have a fancy showroom to pay for.
            </p>
          </motion.div>

          {/* Additional Image Section */}
          <motion.div {...fadeInUp} className="mb-12">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="/assets/assets/images/new/euro-repair-img/bmw-cover-gasket.webp"
                alt="BMW valve cover gasket replacement"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
                <div className="p-8 max-w-lg">
                  <h3 className="text-3xl font-bold text-white mb-2">Common BMW Issue</h3>
                  <p className="text-white/90">Valve cover gasket leaks are one of the most common BMW repairs. Our technicians can diagnose and fix these issues before they cause serious engine damage.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Case Study */}
          <motion.div {...fadeInUp} className="mb-12 bg-blue-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Real Story: The $4,500 Transmission That Wasn't</h3>
            <div className="space-y-3">
              <p className="text-gray-700">
                Guy brings us his 2018 340i from Dallas. "Drivetrain Malfunction" warning, car going into limp mode. Another shop told him he needed a transmission rebuild for $4,500.
              </p>
              <p className="text-gray-700">
                We hook up the scanner and immediately see misfire codes. Not transmission codes—misfire codes. Turns out two of his ignition coils were shot. The car was misfiring so bad it thought the transmission was slipping.
              </p>
              <p className="text-gray-700">
                Fixed it with new coils and plugs. Whole job was under $1,300. Car runs perfect now.
              </p>
              <p className="text-gray-600 italic border-l-4 border-blue-600 pl-4 mt-4">
                This is why you need someone who actually knows BMWs. Not someone who's guessing.
              </p>
            </div>
          </motion.div>

          {/* Testimonial */}
          <motion.div {...fadeInUp} className="mb-12">
            <h3 className="text-3xl font-semibold text-gray-900 mb-6">BMW Models We Service</h3>
            <p className="text-gray-700 mb-6">We work on all BMW models including:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">1 Series</strong><br />
                <span className="text-gray-600">128i, 135i, M1</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">3 Series</strong><br />
                <span className="text-gray-600">320i, 328i, 330i, 335i, 340i, M3</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">5 Series</strong><br />
                <span className="text-gray-600">528i, 530i, 535i, 540i, 550i, M5</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">7 Series</strong><br />
                <span className="text-gray-600">740i, 750i, 760i, Alpina B7</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">X Series</strong><br />
                <span className="text-gray-600">X1, X2, X3, X4, X5, X6, X7</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">Z Series</strong><br />
                <span className="text-gray-600">Z3, Z4, Z8</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">M Series</strong><br />
                <span className="text-gray-600">M2, M3, M4, M5, M6, M8, X3M, X5M</span>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <strong className="text-lg">i Series</strong><br />
                <span className="text-gray-600">i3, i4, i8, iX</span>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-12">
            <h3 className="text-3xl font-semibold text-gray-900 mb-6">Schedule Your BMW Service Today</h3>
            <p className="text-gray-700 mb-8">
              Don't trust your BMW to just anyone. Experience the difference that specialized knowledge 
              and genuine care make. Our team is ready to help keep your BMW running at peak performance.
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-8 rounded-lg">
              <h4 className="font-semibold mb-4 text-xl">Service Hours:</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Clock className="inline-block w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-gray-700">
                    Monday - Friday: 8:00 AM - 6:00 PM<br />
                    Saturday: 9:00 AM - 3:00 PM<br />
                    Sunday: Closed
                  </span>
                </div>
                <div className="flex flex-col gap-4">
                  <Link 
                    to="tel:8173822316"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Clock className="w-5 h-5" />
                    Call (817) 382-2316
                  </Link>
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Schedule Online
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to BMW Inventory */}
          <motion.div {...fadeInUp} className="mb-12">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-10">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Looking to Buy a BMW?</h3>
                  <p className="text-gray-700">
                    We sell pre-owned BMWs inspected by our own technicians. Every vehicle passes the same diagnostic process we use on customer repairs.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/inventory/bmw"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    Browse BMW Inventory <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* BMW Maintenance Cost Section - Featured Snippet Target */}
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">BMW Maintenance Cost in Dallas</h2>
            <p className="text-gray-700 mb-6">
              Let's talk numbers. BMW ownership costs more than your average Honda, but it doesn't have to break the bank if you skip the dealership.
            </p>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service</th>
                    <th className="px-6 py-4 text-left">Dealership Price</th>
                    <th className="px-6 py-4 text-left">Our Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Oil Change (Full Synthetic)</td>
                    <td className="px-6 py-4 text-gray-600">$200 - $350</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$135 - $175</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Brake Pads & Rotors (Front)</td>
                    <td className="px-6 py-4 text-gray-600">$800 - $1,200</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$450 - $700</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Spark Plugs (6-Cylinder)</td>
                    <td className="px-6 py-4 text-gray-600">$400 - $600</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$250 - $400</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Transmission Fluid Service</td>
                    <td className="px-6 py-4 text-gray-600">$500 - $800</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$300 - $450</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Coolant Flush</td>
                    <td className="px-6 py-4 text-gray-600">$250 - $400</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$150 - $225</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Annual Inspection</td>
                    <td className="px-6 py-4 text-gray-600">$150 - $250</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$85 - $125</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm italic">
              * Prices vary by model. M cars and V8s cost more. These are ballpark figures for 3 Series, 5 Series, X3 and X5 models.
            </p>
          </motion.div>

          {/* BMW Maintenance Schedule Section - Featured Snippet Target */}
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">BMW Maintenance Schedule</h2>
            <p className="text-gray-700 mb-6">
              BMW's Condition Based Service system is decent, but here's what we actually recommend based on years of working on these cars in Texas heat:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Every 7,500 - 10,000 Miles</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Oil and filter change (LL-01 spec oil)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Multi-point inspection</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Tire rotation and pressure check</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Top off all fluids</li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Every 2 Years</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Brake fluid flush (don't skip this)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Cabin air filter replacement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Battery test and terminal cleaning</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Brake inspection</li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Every 40,000 - 60,000 Miles</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Engine air filter replacement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Spark plug replacement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Transmission fluid service (yes, even on "lifetime" fluid)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Drive belt inspection/replacement</li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Every 4 Years / 50,000 Miles</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Coolant flush and fill</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Power steering fluid (if applicable)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Differential fluid service</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />Transfer case fluid (xDrive models)</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* BMW FAQ Section - Featured Snippet Optimized */}
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">BMW Service FAQs</h2>
            
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Can I take my BMW to Jiffy Lube for an oil change?</h3>
                <p className="text-gray-700 mb-3">
                  You can, but we really don't recommend it. Here's why:
                </p>
                <ul className="space-y-2 text-gray-600 ml-4 mb-3">
                  <li>• BMWs need European-spec oil (LL-01 or LL-04 rated)—most quick lube shops don't stock it</li>
                  <li>• The oil filter housing on many BMWs requires a special tool and careful torque specs</li>
                  <li>• Your service indicator needs to be properly reset with a scan tool, not just cleared</li>
                  <li>• Wrong oil viscosity can cause timing chain issues, especially on N20 and N55 engines</li>
                </ul>
                <p className="text-gray-700">
                  We've fixed plenty of problems caused by quick lube shops using the wrong oil or overtorquing drain plugs. Save yourself the headache.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">How long is the BMW warranty?</h3>
                <p className="text-gray-700 mb-3">
                  New BMWs come with a <strong>4-year/50,000-mile limited warranty</strong>. Here's what's covered:
                </p>
                <ul className="space-y-2 text-gray-600 ml-4 mb-3">
                  <li>• <strong>Basic warranty:</strong> 4 years/50,000 miles—covers pretty much everything except wear items</li>
                  <li>• <strong>Rust perforation:</strong> 12 years, unlimited miles</li>
                  <li>• <strong>Emissions:</strong> 8 years/80,000 miles on major components</li>
                  <li>• <strong>Battery (hybrids):</strong> 8 years/80,000 miles</li>
                </ul>
                <p className="text-gray-700">
                  BMW also offers extended warranties up to 7 years/100,000 miles. And no, getting your car serviced at an independent shop doesn't void your warranty—that's federal law.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What are the most common BMW problems?</h3>
                <p className="text-gray-700 mb-3">
                  After years of working on BMWs in Dallas, these are what we see constantly:
                </p>
                <ul className="space-y-2 text-gray-600 ml-4 mb-3">
                  <li>• <strong>Oil leaks:</strong> Valve cover gasket, oil filter housing gasket, oil pan gasket—pick one (or all three)</li>
                  <li>• <strong>Cooling system failures:</strong> Water pump, thermostat, expansion tank. Texas heat kills these faster</li>
                  <li>• <strong>VANOS issues:</strong> Solenoids get gunked up, especially with extended oil change intervals</li>
                  <li>• <strong>Electric water pump failure:</strong> Usually around 80k miles on N52 and N54 engines</li>
                  <li>• <strong>Window regulators:</strong> The clips break. It's annoying but cheap to fix</li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Is BMW maintenance more expensive than other cars?</h3>
                <p className="text-gray-700 mb-3">
                  Honestly? Yes. But it's not as bad as people think if you avoid the dealership.
                </p>
                <p className="text-gray-700 mb-3">
                  BMW parts cost more than Honda parts. That's just reality. But the labor rates at dealerships are what really kills you—we're talking $180-$220/hour vs our $120-$140/hour.
                </p>
                <p className="text-gray-700">
                  Annual maintenance runs about $1,000-$1,500 for most BMW owners. That's maybe $300-400 more than a Camry. Not cheap, but not crazy either if you budget for it.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Do you work on older BMWs?</h3>
                <p className="text-gray-700">
                  We love the classics. E30s, E36s, E46s, E39s—those are the good ones before everything got complicated with iDrive and turbo everything. One of our techs daily drives an E39 M5, so yeah, we know these cars. Parts can be harder to source sometimes, but we've got connections with specialists and salvage yards nationwide.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Will getting my BMW serviced at your shop void my warranty?</h3>
                <p className="text-gray-700 mb-3">
                  Nope. Federal law (Magnuson-Moss Warranty Act) says manufacturers can't require you to use dealer service to maintain your warranty. As long as we use the correct parts and fluids—which we do—your warranty stays intact.
                </p>
                <p className="text-gray-700">
                  If a dealer tries to deny a warranty claim because you got an oil change somewhere else, they're bluffing. We'll even provide documentation of all services performed if you need it.
                </p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <div className="bg-blue-600 py-16">
            <div className="max-w-4xl mx-auto text-center px-4">
              <h2 className="text-3xl font-bold text-white mb-4">BMW Repair Dallas - Let's Get Your Car Fixed</h2>
              <p className="text-xl text-white mb-3">
                Tired of dealership prices? Tired of shops that don't know what they're doing? Give us a call.
              </p>
              <p className="text-lg text-blue-100 mb-8">
                We're in Paradise, TX—about 30-40 minutes from most of Dallas. Worth the drive.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:8173822316"
                  className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Call (817) 382-2316
                </a>
                <Link
                  to="/contact"
                  className="inline-block px-8 py-4 bg-blue-800 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Book Now
                </Link>
              </div>
              <p className="text-blue-100 mt-6 italic">
                Same-day appointments available for check engine lights.
              </p>
            </div>
          </div>

          {/* Related Services - Internal Linking for SEO */}
          <div className="max-w-7xl mx-auto px-4 py-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related European Auto Repair Services in Dallas</h3>
            <p className="text-gray-700 mb-6">
              As part of our comprehensive <Link to="/services/european-car-repair" className="text-blue-600 hover:underline font-semibold">European auto repair Dallas</Link> services, 
              we also specialize in servicing other premium German and European vehicles:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Link to="/services/european-car-repair/porsche" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-red-600">Porsche Repair Dallas</h4>
                <p className="text-gray-600 text-sm">IMS bearing experts, PIWIS diagnostics. 911, Cayenne, Boxster specialists.</p>
              </Link>
              <Link to="/services/european-car-repair/audi" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-red-600">Audi Repair Dallas</h4>
                <p className="text-gray-600 text-sm">Quattro specialists. DSG transmission, TFSI engine experts.</p>
              </Link>
              <Link to="/services/european-car-repair/mercedes" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-gray-800">Mercedes Repair Dallas</h4>
                <p className="text-gray-600 text-sm">STAR diagnostics, AIRMATIC suspension, AMG performance service.</p>
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/services/oil-change" className="text-blue-600 hover:underline">BMW Oil Change Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/transmission-repair" className="text-blue-600 hover:underline">BMW Transmission Repair</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/suspension-repair" className="text-blue-600 hover:underline">BMW Suspension Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/european-car-repair" className="text-blue-600 hover:underline">All European Auto Repair Dallas Services</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BMWService;
